import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Line, Group, Rect } from 'react-konva';
import type Konva from 'konva';
import type { Stroke, PlayerState, RevealMode } from '@share-paint/shared';
import { nanoid } from 'nanoid';

interface Props {
  /** Tamaño lógico: sistema de coordenadas en el que se dibujaron los trazos
   *  (ej. 900×600 durante game, 600×400 al pintar cada tarjeta de votación). */
  width: number;
  height: number;
  strokes: Stroke[];
  myUserId: string;
  players: PlayerState[];
  disabled?: boolean;
  myColor?: string;
  brushWidth?: number;
  revealMode?: RevealMode;
  revealTargetId?: string | null;
  onLocalStroke?: (s: Stroke) => void;
}

const CANVAS_BG = '#fafaf7';

function strokeToPoints(s: Stroke): number[] {
  const flat: number[] = [];
  for (const p of s.points) {
    flat.push(p.x, p.y);
  }
  return flat;
}

function shouldShow(
  s: Stroke,
  mode: RevealMode,
  viewerId: string,
  targetId: string | null,
): { color: string; opacity: number; visible: boolean } {
  if (mode === 'all') return { color: s.color, opacity: 1, visible: true };
  if (mode === 'anon') return { color: '#1f2937', opacity: 0.8, visible: true };
  if (mode === 'byUser' && targetId) {
    if (s.authorId === targetId) return { color: s.color, opacity: 1, visible: true };
    return { color: '#1f2937', opacity: 0.15, visible: true };
  }
  return { color: s.color, opacity: 1, visible: true };
}

export function DrawingCanvas({
  width,
  height,
  strokes,
  myUserId,
  players,
  disabled,
  myColor = '#111111',
  brushWidth = 4,
  revealMode = 'all',
  revealTargetId,
  onLocalStroke,
}: Props) {
  const [drawing, setDrawing] = useState<Stroke | null>(null);
  const isDrawing = useRef(false);
  const groupRef = useRef<Konva.Group>(null);

  // El tamaño DOM del <canvas> Konva debe coincidir con el wrapper para que NO
  // se recorte nada. El contenido (trazos en coords lógicas width × height) se
  // escala con scaleX/scaleY para encajar sin perder calidad.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [display, setDisplay] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const compute = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setDisplay((prev) =>
          prev && Math.abs(prev.w - rect.width) < 0.5 && Math.abs(prev.h - rect.height) < 0.5
            ? prev
            : { w: rect.width, h: rect.height },
        );
      }
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const colorByUser = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of players) m.set(p.id, p.color);
    return m;
  }, [players]);

  const startStroke = useCallback(
    (x: number, y: number) => {
      if (disabled) return;
      isDrawing.current = true;
      const s: Stroke = {
        id: nanoid(),
        authorId: myUserId,
        type: 'path',
        color: myColor,
        width: brushWidth,
        points: [{ x, y, t: Date.now() }],
        createdAt: Date.now(),
      };
      setDrawing(s);
    },
    [disabled, myUserId, myColor, brushWidth],
  );

  const extendStroke = useCallback(
    (x: number, y: number) => {
      if (!isDrawing.current || !drawing) return;
      const next: Stroke = {
        ...drawing,
        points: [...drawing.points, { x, y, t: Date.now() }],
      };
      setDrawing(next);
    },
    [drawing],
  );

  const endStroke = useCallback(() => {
    if (!isDrawing.current || !drawing) return;
    isDrawing.current = false;
    onLocalStroke?.(drawing);
    setDrawing(null);
  }, [drawing, onLocalStroke]);

  const stageHandlers = disabled
    ? {}
    : {
        onPointerDown: (e: Konva.KonvaEventObject<PointerEvent>) => {
          const stage = e.target.getStage();
          if (!stage) return;
          const pos = stage.getPointerPosition();
          if (!pos) return;
          // getPointerPosition devuelve coords de píxel; convertimos a coords
          // lógicas (width × height) para que todos los dispositivos almacenen
          // y dibujen los trazos en el mismo sistema, independientemente del
          // tamaño del wrapper.
          const sx = stage.scaleX() || 1;
          const sy = stage.scaleY() || 1;
          startStroke(pos.x / sx, pos.y / sy);
        },
        onPointerMove: (e: Konva.KonvaEventObject<PointerEvent>) => {
          if (!isDrawing.current) return;
          const stage = e.target.getStage();
          if (!stage) return;
          const pos = stage.getPointerPosition();
          if (!pos) return;
          const sx = stage.scaleX() || 1;
          const sy = stage.scaleY() || 1;
          extendStroke(pos.x / sx, pos.y / sy);
        },
        onPointerUp: () => endStroke(),
        onPointerLeave: () => endStroke(),
      };

  // Mientras no tengamos tamaño medido, dibujamos al tamaño lógico. Cuando
  // ResizeObserver obtenga las dimensiones reales, las aplicamos con scale.
  const dw = display?.w ?? width;
  const dh = display?.h ?? height;
  const sx = dw / width;
  const sy = dh / height;

  return (
    <div
      ref={wrapperRef}
      className="relative w-full max-w-full overflow-hidden"
      style={{
        aspectRatio: `${width} / ${height}`,
        touchAction: disabled ? 'pan-y' : 'none',
        overscrollBehavior: 'contain',
      }}
    >
      <Stage
        width={dw}
        height={dh}
        scaleX={sx}
        scaleY={sy}
        {...stageHandlers}
        listening={!disabled}
        style={{
          background: CANVAS_BG,
          borderRadius: 12,
          cursor: disabled ? 'default' : 'crosshair',
          display: 'block',
        }}
      >
        <Layer listening={false}>
          <Rect x={0} y={0} width={width} height={height} fill={CANVAS_BG} />
          <Group ref={groupRef}>
            {strokes.map((s) => {
              const override = shouldShow(s, revealMode, myUserId, revealTargetId ?? null);
              return (
                <Line
                  key={s.id}
                  points={strokeToPoints(s)}
                  stroke={override.color}
                  strokeWidth={s.width}
                  tension={0.4}
                  lineCap="round"
                  lineJoin="round"
                  opacity={override.opacity}
                  listening={false}
                />
              );
            })}
            {drawing && (
              <Line
                points={strokeToPoints(drawing)}
                stroke={drawing.color}
                strokeWidth={drawing.width}
                tension={0.4}
                lineCap="round"
                lineJoin="round"
                listening={false}
              />
            )}
          </Group>
        </Layer>
      </Stage>
    </div>
  );
}
